import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { ConfigService } from 'src/app/services/config/config.service';
import { DataFarmViewModel } from 'src/app/viewModels/dataFarmViewModel';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformerViewModel } from '../../../transformer-list/transformer-list.component';
import { ColumnCleanerRuleViewModel, MatchValueEntryService } from './columnCleanerRuleViewModel';
import { TransformDetailViewModelBase } from '../../../dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';
import { DataDictionaryService } from '../../../../services/dataDictionary/data-dictionary.service';
import { RequiredFieldValidation, ValidationInfo } from 'src/app/objectModel/validationInfo';
import { TransformerParametersViewModel } from '../transformer-parameters/transformerParametersViewModel';
import { ColumnCleanerTransform, ColumnCleanerTransformConfig } from 'src/app/domainObjects/transformers/columnCleanerTransform';
import { ColumnCleanerTransformPreviewDataResponse } from 'src/app/messaging/columnCleanerTransformPreviewDataResponse';
import { IncludeInfo } from 'src/app/domainObjects/includeInfo';
import { TransformType } from 'src/app/domainObjects/transformType';

export class ColumnCleanerViewModel extends TransformDetailViewModelBase<ColumnCleanerTransform> {
  farmType: DataFarmType;
  sampleStart : number;
  sampleEnd : number;
  runAll: boolean;

  subTransformType : RuleType;
  
  rules: Array<ColumnCleanerRuleViewModel> = [];

  parameters:TransformerParametersViewModel;
  // advanced settings
  threads: number;
  resetRows: boolean;
  findWildCard: string;
  
  dataFarm: DataFarmViewModel;
  // Preview options
  rows: number;
  // Status
  rowsDeleted?: number;
  rowsRemaining?: number;
  valuesModified?: number;
  variantCount?: number;

  request: PreviewDataImportRequest;
  response: ColumnCleanerTransformPreviewDataResponse;

    constructor(
      dataDictService: DataDictionaryService,
      private configService: ConfigService,
      configViewModel: DataTransformConfigViewModel,
      transformViewModel: TransformerViewModel,private matchValueEntryService: MatchValueEntryService) {
        super(dataDictService, configViewModel, transformViewModel);

        const transformConfig = this.transform.transformConfig;
        this.parameters=new TransformerParametersViewModel();
        if (transformConfig) {
          this.farmType = transformConfig.farmType ?? transformViewModel.transform.dataFarmType;
          this.sampleEnd = transformConfig.sampleEnd+1;
          this.sampleStart = transformConfig.sampleStart+1;
          this.runAll = transformConfig.runAll;
          this.sortInfo = transformConfig.sortInfo;
          if(transformConfig.includeInResults){
            this.includeInResults = transformConfig.includeInResults.map(item =>new IncludeInfo(item));
            this.parameters.includeInResults=this.includeInResults;
          }
          //parameters
          this.parameters.sortInfo= transformConfig.sortInfo;
          this.parameters.sampleStart = transformConfig.sampleStart+ 1;
          this.parameters.sampleEnd = transformConfig.sampleEnd+ 1;
          this.parameters.runAll= transformConfig.runAll;
    
          // advanced settings
           this.threads = transformConfig.threads;
           this.resetRows = transformConfig.resetRows;
           this.findWildCard = transformConfig.findWildCard;

          if (transformConfig.rules) {
            this.rules = transformConfig.rules.map(r => new ColumnCleanerRuleViewModel(r,this.matchValueEntryService));
          }
        }
    }

    override configureValidationInfo(): ValidationInfo[] {
      const infos = [];
      infos.push( new RequiredFieldValidation('findWildCard', 'Find Wildcard'));
      return infos;
    }

    reindexRules(): void {
      for (let i = 0; i < this.rules.length; i++) {
        this.rules[i].index = i + 1;
      }
      this.rules.sort((a, b) => a.index - b.index);
    }

    public override toTransformConfig(): ColumnCleanerTransformConfig {
      const config = new ColumnCleanerTransformConfig();
      config.farmType = this.farmType;
      config.transformType = TransformType.columnCleaner;
      if(this.parameters.sortInfo){
        if(this.parameters.sortInfo.length==0){
          config.sortInfo =undefined;
        }else{
          config.sortInfo=this.parameters.sortInfo;
        }
      }
      config.sampleStart =this.parameters.sampleStart-1;
      config.sampleEnd =this.parameters.sampleEnd-1;
      if(this.parameters.includeInResults){
        if(this.parameters.includeInResults.length==0){
          config.includeInResults =undefined;
        }else{
          config.includeInResults =this.parameters.includeInResults.map(item => item.includeField);
        }
      }
      config.runAll=this.parameters.runAll;

      config.threads = this.threads;
      config.resetRows = this.resetRows;
      config.findWildCard = this.findWildCard;
      
      config.rules = this.rules.map(r => {
        if(r.ruleType==RuleType.deleteRows){
          return r.toRuleDeleteRows();
        }else if(r.ruleType==RuleType.deDuplicate){
          return r.toRuleDeDuplicate();
        }else if(r.ruleType==RuleType.findReplace){
          return r.toRuleFindReplace();
        }else if(r.ruleType==RuleType.findReplaceAdv){
          return r.toRuleFindReplaceAdv();
        }else if(r.ruleType==RuleType.deleteColumns){
          return r.toRuleDeleteColumns();
        }else if(r.ruleType==RuleType.combineColumns){
          return r.toRuleCombineColumns();
        }else if(r.ruleType==RuleType.splitColumns){
          return r.toRuleSplitColumns();
        }else if(r.ruleType==RuleType.moveColumns){
          return r.toRuleMoveColumns();
        }else if(r.ruleType==RuleType.formatColumns){
          return r.toRuleFormatColumns();
        }else if(r.ruleType==RuleType.splitintowords){
          return r.toRuleSplitIntoWords();
        }else if(r.ruleType==RuleType.removeChars){
          return r.toRuleRemoveCharacters();
        }else{
          return r.toRule();
        }
      });

      return config;
    }

    public override onTransformRunCompleted(response: ColumnCleanerTransformPreviewDataResponse): void {
      // A cache-hit run carries no stats at all (they're deliberately stripped
      // before a CachedProcessingResult is persisted -- see
      // project_stats_vs_fullrun_separation memory). Nothing has changed since
      // whatever last populated the stats currently on screen, so they're still
      // accurate -- leave them exactly as they are instead of wiping them blank.
      if (
        !response?.totalStats ||
        !response.totalStats.globalStat ||
        !response.totalStats.ruleStats
      ) {
        return;
      }

      // Clear all stats prior to rebinding new values
      this.rowsDeleted = null;
      this.rowsRemaining = null;
      this.valuesModified = null;
      this.variantCount = null;
      this.rules.forEach( r => {
        r.rowsDeleted = null;
        r.rowsRemaining = null;
        r.valuesModified = null;
        r.groupHits = null;
      });

      this.rowsDeleted = response.totalStats?.globalStat.rowsDeleted;
      this.rowsRemaining = response.totalStats?.globalStat.rowsRemaining;
      this.valuesModified = response.totalStats?.globalStat.valuesModified;
      this.variantCount = response.totalStats?.globalStat.groupHits;

      this.rows = this.parameters.runAll ? response.currentData?.totalRows : response.activeData?.totalRows;

      // map the rule stats to the rule
      if (response.totalStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.totalStats.ruleStats.find(r => r.ruleIndex === i);
          this.rules[i].rowsDeleted = ruleStat?.rowsDeleted;
          this.rules[i].rowsRemaining = ruleStat?.rowsRemaining;
          this.rules[i].valuesModified = ruleStat?.valuesModified;
          this.rules[i].groupHits = ruleStat?.groupHits;
        }
      }
      // Remove certain Stats for Sub Transformers that don't have these stats
      this.rules.forEach( r => {
        if(r.ruleType==RuleType.deleteColumns
          || r.ruleType==RuleType.splitColumns
          || r.ruleType==RuleType.combineColumns
          || r.ruleType==RuleType.moveColumns
          || r.ruleType==RuleType.formatColumns
          || r.ruleType==RuleType.splitintowords
          || r.ruleType==RuleType.findReplace
          || r.ruleType==RuleType.findReplaceAdv
          || r.ruleType==RuleType.removeChars) {
          r.rowsDeleted = null;
          r.rowsRemaining = null;
        }
        // Variants (distinct find-keys matched) is only meaningful for Find & Replace
        if(r.ruleType!=RuleType.findReplace && r.ruleType!=RuleType.findReplaceAdv) {
          r.groupHits = null;
        }
      });
    }
}

export enum RuleType{
  deleteRows = 0,
  deDuplicate = 1,
  findReplace = 2,
  findReplaceAdv = 3,
  deleteColumns = 4,
  combineColumns = 5,
  splitColumns = 6,
  moveColumns = 7,
  formatColumns = 8,
  splitintowords = 9,
  removeChars = 10
}
export class RuleTypeUtil {
static toString(ruleType: RuleType): string {
    switch (ruleType) {
      case RuleType.deleteRows: return 'Delete Rows';
      case RuleType.deDuplicate: return 'Deduplicate';
      case RuleType.findReplace: return 'Find & Replace';
      case RuleType.findReplaceAdv: return 'Find & Replace (Advanced)';
      case RuleType.deleteColumns: return 'Delete Columns';
      case RuleType.combineColumns: return 'Combine Columns';
      case RuleType.splitColumns: return 'Split Columns';
      case RuleType.moveColumns: return 'Move Columns';
      case RuleType.formatColumns: return 'Format As';
      case RuleType.splitintowords: return 'Split into Words';
      case RuleType.removeChars: return 'Remove Characters';
    }
  }
}


